import React from 'react';
import { XMarkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

// Static reference: the minimum ConnectWise Manage security-role permissions the
// M365Signal API member needs (role "API-M365Signal"). Rendered from data so the
// table stays consistent and easy to update when the integration's needs change.

// Row helper — permissions default to 'None' so only the grants stand out in the data.
const p = (name, { add = 'None', edit = 'None', del = 'None', inquire = 'None', notes = '' } = {}) =>
  ({ name, add, edit, del, inquire, notes });

const CW_PERMISSION_SECTIONS = [
  {
    section: 'Companies',
    rows: [
      p('Company Maintenance', { inquire: 'All' }),
      p('Company/Contact Group Maintenance'),
      p('Configuration - Display Passwords'),
      p('Configurations'),
      p('Contacts'),
      p('CRM/Sales Activities'),
      p('Import Contacts'),
      p('Manage Attachments'),
      p('Management'),
      p('Notes'),
      p('Reports'),
      p('Surveys'),
      p('Team Members', { inquire: 'All' }),
      p('Tracks'),
      p('UserCentric'),
    ],
  },
  { section: 'Finance', rows: [p('(all permissions)')] },
  { section: 'Marketing', rows: [p('(all permissions)')] },
  { section: 'Procurement', rows: [p('(all permissions)')] },
  { section: 'Project', rows: [p('(all permissions)')] },
  { section: 'Sales', rows: [p('(all permissions)')] },
  {
    section: 'Service Desk',
    rows: [
      p('Change Approvals'),
      p('Close Service Tickets'),
      p('CloudConsole'),
      p('ConnectWise Chat'),
      p('ConnectWise ScreenConnect'),
      p('ConnectWise View'),
      p('Knowledge Base Approver'),
      p('Knowledge Base Creator'),
      p('Launch Remote Access'),
      p('Merge Tickets'),
      p('Print Service Signoff'),
      p('Reports'),
      p('Resource Scheduling'),
      p('Service Ticket - Dependencies'),
      p('Service Ticket - Finance'),
      p('Service Tickets', { add: 'My', edit: 'My', inquire: 'All' }),
      p('SLA Dashboard'),
      p('SmileBack'),
      p('Ticket Templates'),
    ],
  },
  {
    section: 'System',
    rows: [
      p('Add Licenses'),
      p('API Reports'),
      p('Chat with ConnectWise Manage Support'),
      p('ConnectWise Manage Labs'),
      p('ConnectWise Next'),
      p('ConnectWise Now'),
      p('Custom Menu Entry'),
      p('Data Import'),
      p('Email Audit'),
      p('List View Export'),
      p('Manage Hosted API'),
      p('Marketplace Sharing'),
      p('Mass Maintenance'),
      p('Member Maintenance'),
      p('Member Maintenance - Finance'),
      p('My Account'),
      p('My Company'),
      p('Report Writer'),
      p('Security Roles'),
      p('System Reports'),
      p('Table Setup (customized)', {
        inquire: 'All',
        notes: 'Allowed: Activities/Activity Type, Company/Company Status, Service/Priority, Service/Service Board',
      }),
      p('Today Links'),
    ],
  },
  { section: 'Time & Expense', rows: [p('(all permissions)')] },
];

const SUMMARY_LINES = [
  { path: 'Companies › Company Maintenance', grants: 'Inquire = All' },
  { path: 'Companies › Team Members', grants: 'Inquire = All' },
  { path: 'Service Desk › Service Tickets', grants: 'Add = My, Edit = My, Inquire = All' },
  {
    path: 'System › Table Setup',
    grants: 'Inquire = All',
    detail: 'Customize restricted to: Activities / Activity Type · Company / Company Status · Service / Priority · Service / Service Board',
  },
];

// Permission value cell — grants stand out, 'None' recedes.
function PermCell({ value }) {
  const cls = value === 'All' ? 'text-green-400 font-medium'
    : value === 'My' ? 'text-sky-300 font-medium'
    : 'text-gray-600';
  return <td className={`px-3 py-1.5 text-center whitespace-nowrap ${cls}`}>{value}</td>;
}

export default function CwPermissionGuideDrawer({ onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Drawer */}
      <div className="relative ml-auto w-full max-w-3xl bg-gray-900 border-l border-gray-800 shadow-2xl flex flex-col h-full">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5 text-blue-400" />
            <h2 className="text-base font-semibold text-gray-100">ConnectWise Manage Permission Guide</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm">
          <p className="text-xs text-gray-500 mb-4">
            Minimum security-role permissions for the API member whose keys are saved on this page.
            In ConnectWise Manage, create a security role (e.g.{' '}
            <span className="font-mono text-gray-400">API-M365Signal</span>) with exactly the
            permissions below and assign it to the API member. Everything not listed as a grant
            stays <span className="text-gray-400">None</span>.
          </p>

          {/* Summary — the actual grants */}
          <div className="mb-5 p-3 bg-gray-800/60 border border-gray-700/60 rounded-lg">
            <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Summary — what&rsquo;s actually granted
            </div>
            <ul className="space-y-1.5">
              {SUMMARY_LINES.map((s) => (
                <li key={s.path} className="text-xs">
                  <span className="text-gray-300">{s.path}:</span>{' '}
                  <span className="text-green-400 font-medium">{s.grants}</span>
                  {s.detail && <div className="text-[11px] text-gray-500 ml-4 mt-0.5">{s.detail}</div>}
                </li>
              ))}
            </ul>
          </div>

          {/* Full role table */}
          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Full Security Role Table — API-M365Signal
          </div>
          <div className="border border-gray-800 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-800/80 text-[11px] text-gray-400 uppercase tracking-wider">
                  <th className="text-left px-3 py-2">Permission</th>
                  <th className="text-center px-3 py-2 w-14">Add</th>
                  <th className="text-center px-3 py-2 w-14">Edit</th>
                  <th className="text-center px-3 py-2 w-14">Delete</th>
                  <th className="text-center px-3 py-2 w-16">Inquire</th>
                  <th className="text-left px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {CW_PERMISSION_SECTIONS.map((sec) => (
                  <React.Fragment key={sec.section}>
                    <tr className="bg-gray-800/40">
                      <td colSpan={6} className="px-3 py-1.5 font-semibold text-gray-300">{sec.section}</td>
                    </tr>
                    {sec.rows.map((r) => (
                      <tr key={`${sec.section}|${r.name}`} className="hover:bg-gray-800/30">
                        <td className={`px-3 py-1.5 pl-6 ${r.name === '(all permissions)' ? 'text-gray-600 italic' : 'text-gray-300'}`}>{r.name}</td>
                        <PermCell value={r.add} />
                        <PermCell value={r.edit} />
                        <PermCell value={r.del} />
                        <PermCell value={r.inquire} />
                        <td className="px-3 py-1.5 text-gray-500">{r.notes}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-800 flex justify-end">
          <button onClick={onClose} className="btn-secondary px-4 py-1.5 text-xs">Close</button>
        </div>
      </div>
    </div>
  );
}
